import React, { useEffect, useState, useMemo } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Select, Textarea } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { Badge } from '@/components/ui/Badge';
import { FilterBar } from '@/components/ui/FilterBar';
import { api } from '@/api/client';
import { useAuth } from '@/context/AuthContext';
import { useCurrency, formatCurrency } from '@/hooks/useCurrency';
import { today } from '@/utils/helpers';
import { printDocument } from '@/utils/printDocs';
import { Plus, Printer, Edit, Trash } from 'lucide-react';

const CN_TYPES = ['Supplier', 'Customer'];

const emptyCN = () => ({
  type: 'Supplier',
  rma_id: '',
  customer_id: '',
  supplier_name: '',
  amount: 0,
  credit_date: today(),
  reason: '',
  notes: ''
});

export const CreditNotes = () => {
  const currency = useCurrency();
  const { isAdmin, isSuperAdmin } = useAuth();
  const [notes, setNotes] = useState([]);
  const [customers, setCustomers] = useState({});
  const [rmas, setRmas] = useState({});
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(emptyCN());

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const [cnRes, custRes, rmaRes] = await Promise.all([
        api.getCreditNotes(),
        api.getCustomers(),
        api.getRMAs()
      ]);
      setNotes(cnRes.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
      const cm = {}; custRes.data.forEach(c => { cm[c.id] = c; }); setCustomers(cm);
      const rm = {}; rmaRes.data.forEach(r => { rm[r.id] = r; }); setRmas(rm);
    } catch (e) { console.error(e); }
  };

  const handleNew = () => {
    setEditing(null);
    setFormData(emptyCN());
    setShowForm(true);
  };

  const handleEdit = (cn) => {
    setEditing(cn);
    setFormData({
      type: cn.type,
      rma_id: cn.rma_id || '',
      customer_id: cn.customer_id || '',
      supplier_name: cn.supplier_name || '',
      amount: cn.amount,
      credit_date: cn.credit_date || today(),
      reason: cn.reason || '',
      notes: cn.notes || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (cn) => {
    if (!window.confirm(`Delete credit note ${cn.cn_number}?`)) return;
    try { await api.deleteCreditNote(cn.id); loadAll(); } catch (e) { alert('Failed'); }
  };

  const handlePrint = (cn) => {
    const recipient = cn.type === 'Customer'
      ? (customers[cn.customer_id]?.name || '—')
      : (cn.supplier_name || '—');
    const content = `
      <div class="doc-title-bar">
        <div class="doc-title">Credit Note</div>
        <div class="doc-meta">
          <div><strong>CN #:</strong> ${cn.cn_number || ''}</div>
          <div><strong>Date:</strong> ${cn.credit_date || ''}</div>
          <div><strong>Type:</strong> ${cn.type}</div>
        </div>
      </div>
      <div class="info-grid">
        <div class="info-card">
          <h3>${cn.type === 'Customer' ? 'Issued To (Customer)' : 'Received From (Supplier)'}</h3>
          <p><strong>${recipient}</strong></p>
          ${cn.rma_id && rmas[cn.rma_id] ? `<p>Linked RMA: <strong>${rmas[cn.rma_id].job_id || cn.rma_id}</strong></p>` : ''}
        </div>
        <div class="info-card">
          <h3>Credit Details</h3>
          <p><strong>Amount:</strong> ${formatCurrency(cn.amount, currency)}</p>
          <p><strong>Used:</strong> ${formatCurrency(cn.used_amount || 0, currency)}</p>
          <p><strong>Remaining:</strong> ${formatCurrency((cn.amount || 0) - (cn.used_amount || 0), currency)}</p>
          <p><strong>Status:</strong> ${cn.status}</p>
        </div>
      </div>
      <table class="doc-table">
        <thead><tr><th>Field</th><th>Details</th></tr></thead>
        <tbody>
          <tr><td><strong>Reason</strong></td><td>${cn.reason || ''}</td></tr>
          ${cn.notes ? `<tr><td><strong>Notes</strong></td><td>${cn.notes}</td></tr>` : ''}
        </tbody>
      </table>
      <div class="signature-grid">
        <div class="sig-box">Authorized Signature</div>
        <div class="sig-box">Recipient Signature</div>
      </div>
    `;
    printDocument({ title: cn.cn_number || 'Credit Note', content });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.amount || formData.amount <= 0) { alert('Enter a valid amount'); return; }
    if (!formData.reason) { alert('Reason is required'); return; }
    if (formData.type === 'Customer' && !formData.customer_id) { alert('Select a customer'); return; }
    if (formData.type === 'Supplier' && !formData.supplier_name) { alert('Enter supplier name'); return; }

    setLoading(true);
    try {
      const payload = { ...formData, amount: parseFloat(formData.amount) };
      if (editing) {
        await api.updateCreditNote(editing.id, payload);
      } else {
        await api.createCreditNote(payload);
      }
      setShowForm(false);
      loadAll();
    } catch (err) {
      alert('Failed to save credit note');
    } finally { setLoading(false); }
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return notes.filter(cn => {
      if (typeFilter && cn.type !== typeFilter) return false;
      if (!q) return true;
      const cust = customers[cn.customer_id];
      return (cn.cn_number || '').toLowerCase().includes(q)
        || (cn.reason || '').toLowerCase().includes(q)
        || (cn.supplier_name || '').toLowerCase().includes(q)
        || (cust?.name || '').toLowerCase().includes(q)
        || (cust?.phone || '').includes(q);
    });
  }, [notes, search, typeFilter, customers]);

  const totals = useMemo(() => {
    let outstanding = 0, total = 0;
    notes.forEach(cn => {
      const remaining = (cn.amount || 0) - (cn.used_amount || 0);
      total += (cn.amount || 0);
      if (cn.status !== 'Void' && remaining > 0) outstanding += remaining;
    });
    return { outstanding, total };
  }, [notes]);

  return (
    <div className="space-y-6" data-testid="credit-notes-page">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-medium tracking-tight text-[var(--text-primary)]">Credit Notes</h1>
        <Button onClick={handleNew} data-testid="new-cn-button">
          <Plus size={18} className="mr-2" /> New Credit Note
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <p className="text-xs uppercase tracking-wide text-[var(--text-muted)]">Total Issued</p>
          <p className="text-2xl font-semibold mt-1" data-testid="cn-total">{formatCurrency(totals.total, currency)}</p>
        </Card>
        <Card>
          <p className="text-xs uppercase tracking-wide text-[var(--text-muted)]">Outstanding</p>
          <p className="text-2xl font-semibold mt-1 text-amber-600 dark:text-amber-400" data-testid="cn-outstanding">{formatCurrency(totals.outstanding, currency)}</p>
        </Card>
      </div>

      <Card>
        <FilterBar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search CN #, customer, supplier, reason..."
          filters={[{ key: 'type', label: 'All Types', value: typeFilter, options: CN_TYPES, onChange: setTypeFilter }]}
          onClear={() => { setSearch(''); setTypeFilter(''); }}
          testId="cn-filter"
        />
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>CN #</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Party</TableHead>
              <TableHead>Linked RMA</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Used</TableHead>
              <TableHead>Remaining</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow><TableCell colSpan={10} className="text-center py-8 text-[var(--text-muted)]">No credit notes</TableCell></TableRow>
            ) : filtered.map(cn => {
              const remaining = (cn.amount || 0) - (cn.used_amount || 0);
              return (
                <TableRow key={cn.id}>
                  <TableCell className="font-mono text-xs font-semibold">{cn.cn_number}</TableCell>
                  <TableCell><Badge variant={cn.type === 'Customer' ? 'info' : 'default'}>{cn.type}</Badge></TableCell>
                  <TableCell>{cn.type === 'Customer' ? (customers[cn.customer_id]?.name || '—') : (cn.supplier_name || '—')}</TableCell>
                  <TableCell className="font-mono text-xs">{cn.rma_id && rmas[cn.rma_id] ? (rmas[cn.rma_id].job_id || cn.rma_id.slice(0, 8)) : '—'}</TableCell>
                  <TableCell className="font-semibold">{formatCurrency(cn.amount, currency)}</TableCell>
                  <TableCell>{formatCurrency(cn.used_amount || 0, currency)}</TableCell>
                  <TableCell className={remaining > 0 ? 'text-amber-600 dark:text-amber-400 font-semibold' : ''}>{formatCurrency(remaining, currency)}</TableCell>
                  <TableCell><Badge variant={cn.status === 'Outstanding' ? 'warning' : (cn.status === 'Void' ? 'danger' : 'success')}>{cn.status}</Badge></TableCell>
                  <TableCell>{cn.credit_date}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => handleEdit(cn)} data-testid={`edit-cn-${cn.id}`}><Edit size={16} /></Button>
                      <Button size="sm" variant="ghost" onClick={() => handlePrint(cn)} data-testid={`print-cn-${cn.id}`}><Printer size={16} /></Button>
                      {isSuperAdmin() && (
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(cn)} data-testid={`delete-cn-${cn.id}`}>
                          <Trash size={16} className="text-red-600" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title={editing ? `Edit ${editing.cn_number}` : 'New Credit Note'} size="md">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Select
              label="Type *"
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              data-testid="cn-type"
            >
              {CN_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </Select>
            <Input
              label="Credit Date *"
              type="date"
              value={formData.credit_date}
              onChange={(e) => setFormData({ ...formData, credit_date: e.target.value })}
              data-testid="cn-date"
            />
          </div>

          {formData.type === 'Customer' ? (
            <Select
              label="Customer *"
              value={formData.customer_id}
              onChange={(e) => setFormData({ ...formData, customer_id: e.target.value })}
              data-testid="cn-customer"
            >
              <option value="">-- Select Customer --</option>
              {Object.values(customers).map(c => <option key={c.id} value={c.id}>{`${c.name} (${c.phone})`}</option>)}
            </Select>
          ) : (
            <Input
              label="Supplier Name *"
              value={formData.supplier_name}
              onChange={(e) => setFormData({ ...formData, supplier_name: e.target.value })}
              data-testid="cn-supplier"
            />
          )}

          <Select
            label="Linked RMA (optional)"
            value={formData.rma_id}
            onChange={(e) => setFormData({ ...formData, rma_id: e.target.value })}
            data-testid="cn-rma"
          >
            <option value="">-- None --</option>
            {Object.values(rmas).map(r => <option key={r.id} value={r.id}>{`${r.job_id || r.id.slice(0, 8)} — ${r.serial_original}`}</option>)}
          </Select>

          <Input
            label="Amount *"
            type="number"
            step="0.01"
            min="0"
            value={formData.amount}
            onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
            data-testid="cn-amount"
          />

          <Input
            label="Reason *"
            value={formData.reason}
            onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
            placeholder="e.g. DOA replacement credited by manufacturer"
            data-testid="cn-reason"
          />
          <Textarea
            label="Notes"
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            rows={2}
            data-testid="cn-notes"
          />

          <div className="flex justify-end gap-2 pt-3 border-t border-[var(--border)]">
            <Button type="button" variant="secondary" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={loading} data-testid="save-cn">
              {loading ? 'Saving...' : (editing ? 'Update' : 'Create')}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};
